import {LitElement, html, css} from 'lit';
import {msg} from '@lit/localize';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import store from '../../../store';
import {cssProperties} from '../../../css/cssProperties';
import {baseProfileOptions} from '../../../sectionConfig';
import {downloadPDF} from '../../../slices/downloadSlice';
import loading from '../../../../icons/loading';
import specsheet from '../../../../icons/specsheet';
import {selectLdt1, selectMountingInstruction} from '../../../selectors';

@customElement('visual-lightprofile-summary-header')
export class VisualLightprofileSummaryHeader extends connect(store)(LitElement) {

	static styles = [
		css`
		  .headerSection {
			position: relative;
		  }
		.headerContent {
			color: #f1f1f1;
			position: absolute;
			padding-left: var(--large-padding);
			top: 100px;
		  }

		.summaryVideo {
			bottom: 0;
			background-color: black;
			background-repeat: no-repeat;
			background-size: cover;
			min-width: 100%;
			padding: 0px;
			position: relative;
			right: 0;
			height: 100%;
			max-height: 80vh;
			object-fit: cover;
    		object-position: center bottom;
			min-height: 66vh;
		  }

		  .overlay {
			background-image: linear-gradient(360deg, rgba(0, 0, 0, 0.3) 2.02%, rgba(0, 0, 0, 0) 13.58%), linear-gradient(90deg, rgba(0, 0, 0, 0.6) 22.75%, rgba(0, 0, 0, 0) 100%);
			opacity: 0.75;
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			width: 100%;
			height: 100%;
			margin: 0;
			box-sizing: border-box;
		  }

		  h5 {
			color: white;
			line-height: 22px;
			letter-spacing: 0.2px;
			font-family: var(--font-family-light);
			font-size: 12px;
			font-style: normal;
			font-weight: 400;
		  }

		  h1 {
			color: #FFFFFF;
			font: var(--font-modal-title);
			letter-spacing: 0.4px;
			line-height: 34px;
		  }

		  h1 span {
			font: var(--font-modal-title-regular);
		  }

		  .buttonsGroup {
			display: flex;
			column-gap: 5px;
		  }

		  button, .numbers, .button, .email {
			display: flex;
			background-color: transparent;
			box-shadow: none;
			border: 1px solid #fff;
			color: #fff;
			font-family: var(--font-family-light);
			font-size: 12px;
			font-style: normal;
			font-weight: 400;
			line-height: 19px;
			padding: 5px 10px;
			cursor: pointer;
			text-decoration: none;
		  }
		.email {
			font: var(--font-info-light);
		}
		  div.cta-email {
			position: absolute;
			bottom: var(--large-padding);
			left: var(--large-padding);
			}

		  .button.loading {
		  	pointer-events: none;
			opacity: 0.7;
		  }
			.numbers {
				cursor: pointer;
			}

		.icon {
			display: inline-block;
			height: 16px;
			width: 16px;
			margin-right: 10px;
			color: var(--color-text-widget-title);
		}
		.icon svg {
			width: 100%;
			height: auto;
			fill: currentColor !important;
		}
		.icon svg * {
			stroke: currentColor !important;
		}

          @media (max-width: 768px) {
            .headerContent {
              padding-left: var(--small-padding);
            }
			div.cta-email {
				left: var(--small-padding);
				}
          }

		`
	];


	videoSrc = '';

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}
	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.translations = state.productData.translations;
		this.decisions = state.decisions.data;
		this.profile = state.lightProfile.data.profile;
		this.lightProfile = state.lightProfile.data;
		this.lang = state.status.lang;
		this.downloadStatus = state.download.status;
		this.active = state.status.specsheetActive;
		this.baseProfileOptions = this.baseProfileData(this.lightProfile.project.base);
		this.projectName = this.lightProfile.project.projectName;
		this.shortNumberProductModel = state.shortNumber.data.ProductModel ?? '';
		this.shortNumberCircularProfile = state.shortNumber.data.CircularProfile ?? '';
		if (this.baseProfileOptions && this.baseProfileOptions.video !== this.videoSrc) {
			this.videoSrc = this.baseProfileOptions.video;
			if (this.shadowRoot.querySelector('video')) {
				this.shadowRoot.querySelector('video').load();
			}
		}
	}

	downloadPDF(e) {
		store.dispatch(downloadPDF());
	}

	baseProfileData(id) {
		return baseProfileOptions.find(baseProfileOption => baseProfileOption.id === id);
	}

	render() {
		const s = this.state;
		const label = this.baseProfileOptions ? this.baseProfileOptions.label[this.lang] : '';
		const image = this.baseProfileOptions ? this.baseProfileOptions.image : '';
		return html`
			<section class="headerSection">

				<video autoplay muted loop playsinline class="summaryVideo">
					<source src="${this.videoSrc}" type="video/mp4">
				</video>

				<div class="overlay"></div>

				<div class="headerContent">
					<h5>${msg('Your light profile')}</h5>
					<h1>
						<span>${label}</span>
						${when(this.projectName && this.projectName !== '',
							() => html`
								${msg('for')} <br> ${this.projectName}
							`)}
					</h1>
					<div class="buttonsGroup">
						<a href="${this.status.host}${this.status.baseUrl}${this.status.region}/${this.status.lang}/${this.status.family}/sections/${this.status.mode}/${this.status.shortKey}"
						   class="numbers">
							${this.shortNumberCircularProfile}
							${when(this.shortNumberProductModel !== '', () => html`- ${this.shortNumberProductModel}`)}</a>
						<button class="button pdf ${classMap({loading: this.downloadStatus === 'loading'})}" @click="${this.downloadPDF}">
							<span class="icon">${this.downloadStatus === 'loading' ? loading : specsheet}</span>
							<span class="label strong">${msg('PDF')}</span>
						</button>
						${when(selectLdt1(this.state) !== null, () => html`
							<a class="button" href="${selectLdt1(this.state)}" target="_blank">
								<span class="icon">${specsheet}</span>
								<span class="label">${msg('LDT')}</span>
							</a>
						`)}
						${when(selectMountingInstruction(this.state) !== null, () => html`
							<a class="button" href="${selectMountingInstruction(this.state)}" target="_blank">
								<span class="icon">${specsheet}</span>
								<span class="label">${msg('Mounting Instructions')}</span>
							</a>
						`)}

					</div>
				</div>
				<div class="cta-email">
					<a class='email' title="send email" target="_blank" href="https://www.selux.com/${this.status.region}/${this.status.lang}/contacts/circular-light-profile-request?lp=${this.status.shortKey}">
					${msg('Contact us for more information')}
					</a>
				</div>
			</section>
		`;
	}
}
